import { renderToStaticMarkup } from "react-dom/server";
import { Citation } from "../Answer/types";
import linkIcon from "../../assets/external-link.svg";
import downloadIcon from "../../assets/download.svg";

// Regexes for detecting URLs and email addresses
const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;
const emailRegex = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/gi;

/**
 * Convert URLs and email addresses in a text string into clickable links.
 */
function linkify(text: string): string {
    return text
        .replace(urlRegex, (match) => {
            let href = match;
            if (!/^https?:\/\//i.test(match)) {
                href = "http://" + match;
            }
            return (
                '<a class="textLink" href="' +
                href +
                '" target="_blank" rel="noopener noreferrer">' +
                match +
                "</a>"
            );
        })
        .replace(emailRegex, (match) => {
            return (
                '<a class="textLink" href="mailto:' +
                match +
                '" target="_blank" rel="noopener noreferrer">' +
                match +
                "</a>"
            );
        });
}

/**
 * Convert basic Markdown (bold, lists) into nested HTML lists.
 */
function markdownToHtml(text: string): string {
    const lines = text.split("\n");
    let html = "";
    const stack: Array<{ type: "ul" | "ol"; indent: number }> = [];

    function closeToIndent(indent: number) {
        while (stack.length && stack[stack.length - 1].indent > indent) {
            const last = stack.pop()!;
            html += "</" + last.type + ">";
        }
    }

    lines.forEach((rawLine) => {
        if (!rawLine.trim()) return;
        const converted = rawLine.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        const indentMatch = rawLine.match(/^(\s*)/);
        const indent = indentMatch ? indentMatch[1].length : 0;
        const trimmed = converted.trim();

        const olMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
        const ulMatch = trimmed.match(/^- (.*)$/);

        if (olMatch) {
            closeToIndent(indent);
            if (!stack.length || stack[stack.length - 1].type !== "ol") {
                stack.push({ type: "ol", indent });
                html += "<ol>";
            }
            html += "<li>" + linkify(olMatch[2]) + "</li>";
        } else if (ulMatch) {
            closeToIndent(indent);
            if (!stack.length || stack[stack.length - 1].type !== "ul") {
                stack.push({ type: "ul", indent });
                html += "<ul>";
            }
            html += "<li>" + linkify(ulMatch[1]) + "</li>";
        } else {
            // close any open lists before paragraph
            closeToIndent(-1);
            html += "<p>" + linkify(converted) + "</p>";
        }
    });

    closeToIndent(-1);
    return html;
}

export function parseAnswerToHtml(
    answer: string,
    isStreaming: boolean,
    dataPoints: Array<{
        id: string;
        source_id: string;
        metadata: {
            document_url: string;
            document_guid: string;
            last_modified_date: string;
            file_name: string;
            title: string;
            type: string;
        };
    }>,
    onCitationClicked: (url: string) => void
): { answerHtml: string; citations: Citation[] } {
    // Trim incomplete citation if streaming
    let parsed = answer.trim();
    if (isStreaming) {
        const lastOpen = parsed.lastIndexOf("[");
        const lastClose = parsed.lastIndexOf("]");
        if (lastOpen > lastClose) {
            parsed = parsed.slice(0, lastOpen);
        }
    }

    // 1) Markdown -> HTML
    const htmlMd = markdownToHtml(parsed);

    // Build lookups (minimal change: make source/guid matching case-insensitive)
    const bySourceId = new Map(
        dataPoints
            .filter((d) => d.source_id)
            .map((d) => [d.source_id.toLowerCase(), d] as const)
    );
    const byGuid = new Map(
        dataPoints
            .filter((d) => d.metadata?.document_guid)
            .map((d) => [d.metadata.document_guid.toLowerCase(), d] as const)
    );
    const byId = new Map(dataPoints.map((d) => [d.id, d] as const));

    // 2) Split out citations, capturing optional trailing dot
    const parts = htmlMd.split(/(\[[^\]]+\]\.??)/g);

    const citations: Citation[] = [];
    const fragments = parts.map((part, idx) => {
        if (idx % 2 === 1) {
            // part is "[ID]" or "[ID]."
            const m = part.match(/^\[([^\]]+)\](\.)?$/);
            if (!m) return part;
            const citationIdRaw = m[1];
            const trailingDot = m[2] || "";
            const keyLC = citationIdRaw.trim().toLowerCase();

            // find or register citation metadata
            let cit = citations.find((c) => c.id === citationIdRaw);
            if (!cit) {
                const dp =
                    bySourceId.get(keyLC) ||
                    byGuid.get(keyLC) ||
                    byId.get(citationIdRaw.trim());

                if (dp) {
                    cit = {
                        id: citationIdRaw,
                        url: dp.metadata.document_url,
                        lastModified: dp.metadata.last_modified_date,
                        file_name: dp.metadata.file_name,
                        title: dp.metadata.title,
                        type: dp.metadata.type,
                    };
                    citations.push(cit);
                }
            }

            if (cit) {
                const currentCit = cit; // TS now knows this is non-undefined
                const index = citations.indexOf(currentCit) + 1;
                const isAsset = currentCit.type.toUpperCase() === "ASSET";
                // both wrapper and tooltip use <span> so HTML stays valid
                return (
                    renderToStaticMarkup(
                        <span className="tooltipWrapper">
                            <span className="supContainer">{index}</span>
                            <span className="tooltipText">
                                <span className="tooltipLine">{currentCit.title}</span>
                                <span className="tooltipDate">
                                    {currentCit.lastModified
                                        ? new Date(currentCit.lastModified).toLocaleDateString("de-CH")
                                        : ""}
                                </span>
                                <a
                                    href={currentCit.url}
                                    className="citationIcon"
                                    aria-label={isAsset ? "Download asset" : "Open source"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-citation-id={currentCit.id}
                                    data-citation-type={currentCit.type}
                                    onClick={() => onCitationClicked(currentCit.url)}
                                >
                                    <img
                                        src={isAsset ? downloadIcon : linkIcon}
                                        alt={isAsset ? "Download asset" : "Open source"}
                                    />
                                </a>
                            </span>
                        </span>
                    ) + trailingDot
                );
            }

            // Suppress unmatched placeholders that start with "source_"
            if (/^source_/i.test(citationIdRaw)) {
                return ""; // no brackets, no inner text, no trailing dot
            }

            // literal fallback + dot for other unmatched bracketed text
            return "[" + citationIdRaw + "]" + trailingDot;
        }
        // even parts are plain text/html
        return part;
    });

    return {
        answerHtml: fragments.join(""),
        citations,
    };
}
